import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TableManager from '../components/data/TableManager';
import { logger } from '../utils/logger';
import DragDropSequenceManager from '../common/DragDropSequenceManager';
import { FaSort } from 'react-icons/fa';
import { apiService } from '../services/apiService';
import { computeValidationType } from '../utils/zodValidationHelper';

// Configuration for SKE_Attributi columns
const columnConfig = [
  // --- Keys and Identifiers ---
  "ATT_Id_Attributi=0,ID Attributo,isPrimaryKey",
  "ATT_Id_Oggetti=1,Oggetto,required,lookupName=oggettiLookup",
  
  "ATT_Nome=2,Nome Attributo,required",
  "ATT_Descrizione=2,Descrizione",
  "ATT_TipiCampi=2,Tipo Campo,lookupName=tipiCampiLookup",
  "ATT_Super_Oggetti=2,Oggetto Padre (Super),lookupName=oggettiLookup", // Basato su DDL
  "ATT_Lunghezza=1,Lunghezza",
  "ATT_Sequenza=2,Sequenza,required,type=number",
  
  // --- Altri campi dal DDL ---
  "ATT_IdRif_Attributi=0,ID Rif. Attributo,type=number",
  "ATT_NomeAttributoChiaveEsterno=1,Nome Attributo Chiave Esterna",
  "ATT_Default=1,Default",
  "ATT_Like=2,Like,type=boolean,isBoolean",
  "ATT_AccettaZero=2,Accetta Zero,type=boolean,isBoolean",
  "ATT_Manuale=2,Manuale,type=boolean,isBoolean", 
  "ATT_Lista=2,Lista,type=boolean,isBoolean",
  "ATT_JsonIgnore=2,Json Ignore,type=boolean,isBoolean",
  "ATT_Id_TipiValidazioni=2,Tipo Validazione,lookupName=tipiValidazioniLookup",

  // --- Validity and Status (Hidden from table by default, visible in forms) ---
  "ATT_InizioValidita=0,Inizio Validità,type=datetime",
  "ATT_FineValidita=0,Fine Validità,type=datetime",
  "ATT_StatiRecord=0,Stato Record",
  "ATT_Utente=0,Utente,type=number",
  "ATT_Terminale=0,Terminale",

  // --- Audit Fields (Hidden, managed by backend/trigger) ---
  "ATT_UtenteInserimento=0,Utente Inserimento",
  "ATT_TerminaleInserimento=0,Terminale Inserimento",
  "ATT_DataOraInserimento=0,Data/Ora Inserimento,type=datetime",
  "ATT_UtenteModifica=0,Utente Modifica",
  "ATT_TerminaleModifica=0,Terminale Modifica",
  "ATT_DataOraModifica=0,Data/Ora Modifica,type=datetime",
  "ATT_Timestamp=0,Timestamp DB,type=datetime",
];

// Configuration for Filters
const filterConfig = [
  { field: 'ATT_Nome', label: 'Nome Attributo', type: 'text' },
  { field: 'ATT_Descrizione', label: 'Descrizione', type: 'text' },
  { field: 'ATT_Id_Oggetti', label: 'Oggetto', type: 'lookup', lookupName: 'oggettiLookup', lookupType: 'select' },
  { field: 'ATT_TipiCampi', label: 'Tipo Campo', type: 'lookup', lookupName: 'tipiCampiLookup', lookupType: 'select' },
  { field: 'ATT_Super_Oggetti', label: 'Oggetto Padre (Super)', type: 'lookup', lookupName: 'oggettiLookup', lookupType: 'select' },
];

// Configuration for Lookups
const initialLookupConfig = {
  oggettiLookup: { 
    tableName: 'SKE_Oggetti',  // ✅ FIX: Usa tableName invece di endpoint
    valueField: 'OGG_Id_Oggetti',
    labelField: 'OGG_Nome',
    // ✅ NEW: Nested lookup configuration for module codes
    nestedLookup: {
      foreignKeyField: 'OGG_Id_Moduli',
      nestedTableName: 'SKE_Moduli',
      nestedValueField: 'MOD_Id_Moduli',
      nestedLabelField: 'MOD_CodiceModulo',
      formatLabel: (objName, moduleCode) => `${objName} (${moduleCode})`,
    },
  },
  tipiCampiLookup: {
    tableName: 'SKE_TipiCampi',  // ✅ FIX: Usa tableName invece di endpoint
    valueField: 'TIP_Id_TipiCampi',
    labelField: 'TIP_Descrizione',
  },
};

// Specific Input Types for Form Fields
const inputTypes = {
  ATT_Descrizione: 'textarea',
  ATT_Sequenza: 'number',
  ATT_IdRif_Attributi: 'number',
  ATT_Id_Oggetti: 'select',
  ATT_TipiCampi: 'select',
  ATT_Super_Oggetti: 'select',
  ATT_Id_TipiValidazioni: 'select',
  ATT_Utente: 'number',
  ATT_InizioValidita: 'datetime-local',
  ATT_FineValidita: 'datetime-local',
  ATT_DataOraInserimento: 'datetime-local',
  ATT_DataOraModifica: 'datetime-local',
  ATT_Timestamp: 'datetime-local',
};

const AttributiPage = () => {
  const location = useLocation();
  
  // ✅ MANTIENE: Funzionalità di navigazione avanzata (per supportare sia navigazione diretta che new-tab)
  const [initialFilters, setInitialFilters] = useState({});
  const [tableManagerKey, setTableManagerKey] = useState(Date.now());

  // State and refs for sequence management
  const [isSequenceModalOpen, setIsSequenceModalOpen] = useState(false);
  const [sequenceData, setSequenceData] = useState([]);
  const updateSequenceRef = React.useRef(null);

  // Mappa TipiCampi: TIP_Id_TipiCampi -> TIP_CodiceTipoTypescript (per calcolo Zod lato browser)
  const tipiCampiMapRef = useRef({});

  const tableName = "SKE_Attributi";
  const primaryKey = "ATT_Id_Attributi";
  const sequenceField = "ATT_Sequenza";

  // Fetch TipiCampi al mount per ottenere la mappa tipoId → TypeScript type
  useEffect(() => {
    const fetchTipiCampi = async () => {
      try {
        const response = await apiService.get('/crud/SKE_TipiCampi', {
          params: {
            limit: 10000,
            offset: 0,
            fields: 'TIP_Id_TipiCampi,TIP_CodiceTipoTypescript',
            filters: JSON.stringify({}),
            orderBy: 'TIP_Id_TipiCampi',
            orderDirection: 'ASC',
          }
        });
        const records = response?.records || response?.data || [];
        if (records.length > 0) {
          const map = {};
          records.forEach(tipo => {
            map[tipo.TIP_Id_TipiCampi] = tipo.TIP_CodiceTipoTypescript || null;
          });
          tipiCampiMapRef.current = map;
          logger.debug('[AttributiPage] TipiCampi map loaded:', map);
        }
      } catch (err) {
        logger.error('[AttributiPage] Errore caricamento TipiCampi:', err);
      }
    };
    fetchTipiCampi();
  }, []);

  // ✅ MANTIENE: Gestione completa per diversi scenari di navigazione
  const parentOggettoIdFromState = useMemo(() => {
    return location.state?.initialFilter_ATT_Id_Oggetti || 
           location.state?.presetFilter?.ATT_Id_Oggetti;
  }, [location.state]);

  // ✅ MANTIENE: Effect completo per gestire tutte le modalità di navigazione
  useEffect(() => {
    const filters = {};
    
    // Check for filters from URL parameters (new tab navigation)
    const urlParams = new URLSearchParams(window.location.search);
    const filterKey = urlParams.get('filterKey');
    
    if (filterKey) {
      let filterData = localStorage.getItem(filterKey) || sessionStorage.getItem(filterKey);
      if (filterData) {
        try {
          const parsedData = JSON.parse(filterData);
          if (parsedData.presetFilter?.ATT_Id_Oggetti) {
            filters.ATT_Id_Oggetti = parsedData.presetFilter.ATT_Id_Oggetti;
            logger.info('[AttributiPage] Received oggettoId from new tab navigation:', parsedData.presetFilter.ATT_Id_Oggetti);
          }
          localStorage.removeItem(filterKey);
          sessionStorage.removeItem(filterKey);
        } catch (error) {
          logger.error('[AttributiPage] Error parsing filter data from storage:', error);
        }
      }
    } else if (parentOggettoIdFromState) {
      filters.ATT_Id_Oggetti = parentOggettoIdFromState;
      logger.info('[AttributiPage] Received oggettoId for filtering ATT_Id_Oggetti:', parentOggettoIdFromState);
    }

    setInitialFilters(filters);
    
    if (Object.keys(filters).length > 0) {
      setTableManagerKey(Date.now());
    }
  }, [parentOggettoIdFromState]);

  // ✅ MANTIENE: Calcola se fare ricerca automatica con filtri iniziali
  const autoSearchOnInitialFilters = useMemo(() => {
    return Object.keys(initialFilters).length > 0;
  }, [initialFilters]);

  // ✅ MANTIENE: Disabilita autoSearchOnLoad quando ci sono filtri iniziali
  const shouldAutoSearchOnLoad = useMemo(() => {
    return Object.keys(initialFilters).length === 0;
  }, [initialFilters]);

  const finalLookupConfig = useMemo(() => {
    return {
      ...initialLookupConfig,
      tipiValidazioniLookup: {
        tableName: 'SKE_TipiValidazioni',
        valueField: 'TIV_Id_TipiValidazioni',
        labelField: 'TIV_Descrizione',
      },
    };
  }, []);

  // Handler per side-effects: quando cambiano ATT_Nome, ATT_TipiCampi o ATT_Lunghezza,
  // ricalcola ATT_Id_TipiValidazioni come suggerimento dinamico (FK verso SKE_TipiValidazioni)
  const handleFieldChange = useCallback((field, value, currentFormData) => {
    const fieldsCheTriggerano = ['ATT_Nome', 'ATT_TipiCampi', 'ATT_Lunghezza'];
    if (!fieldsCheTriggerano.includes(field)) return null;

    const nome = field === 'ATT_Nome' ? value : currentFormData.ATT_Nome;
    const tipoId = field === 'ATT_TipiCampi' ? value : currentFormData.ATT_TipiCampi;
    const lunghezza = field === 'ATT_Lunghezza' ? value : currentFormData.ATT_Lunghezza;
    const superOggetti = currentFormData.ATT_Super_Oggetti || 0;

    // Risolvi il tipo TypeScript dalla mappa TipiCampi
    const tipoTypescript = tipoId ? (tipiCampiMapRef.current[tipoId] || null) : null;

    const nuovaValidazione = computeValidationType(nome, tipoTypescript, lunghezza, tipoId, superOggetti);
    
    logger.debug(`[AttributiPage] onFieldChange: field=${field}, computed ATT_Id_TipiValidazioni=${nuovaValidazione}`);
    return { ATT_Id_TipiValidazioni: nuovaValidazione || '' };
  }, []);

  // ✅ RIPRISTINA: Versione originale più semplice della gestione sequence
  const handleSaveSequence = async (updatedItems) => {
    logger.info(`[AttributiPage] Attempting to save new sequence for ${tableName}.`);
    if (updateSequenceRef.current) {
      const success = await updateSequenceRef.current(updatedItems, sequenceField, primaryKey);
      if (success) {
        setIsSequenceModalOpen(false);
        logger.info(`[AttributiPage] Sequence saved and data refreshed via TableManager.`);
      } else {
        logger.error(`[AttributiPage] TableManager reported an error during sequence update.`);
      }
    } else {
      logger.error('[AttributiPage] updateSequence function ref is not set. Cannot save sequence.');
    }
  };

  // ✅ NUOVO: Calcola i valori di default per la creazione dai filtri attivi
  const defaultValuesForCreate = useMemo(() => {
    const defaults = {};
    
    // Se c'è un filtro per ATT_Id_Oggetti, usalo come default per i nuovi record
    if (initialFilters.ATT_Id_Oggetti) {
      defaults.ATT_Id_Oggetti = initialFilters.ATT_Id_Oggetti;

    }
    
    return defaults;
  }, [initialFilters]);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
        Gestione Attributi
      </h1>

      <TableManager
        key={`attributi-manager-${tableManagerKey}`} // ✅ MANTIENE: Key change forces re-mount for navigation
        tableName={tableName}
        primaryKey={primaryKey}
        pageTitle="Gestione Attributi"
        columnConfig={columnConfig}
        filterConfig={filterConfig}
        lookupConfig={finalLookupConfig}
        inputTypes={inputTypes}
        enableCreate={true}
        enableEdit={true}
        enableDelete={true}
        autoSearchOnLoad={shouldAutoSearchOnLoad} // ✅ MANTIENE: Condizionale per navigazione
        autoSearchOnInitialFilters={autoSearchOnInitialFilters} // ✅ MANTIENE: Ricerca automatica se ci sono filtri
        defaultSortField="ATT_Sequenza"
        defaultSortDirection="asc"
        initialFilters={initialFilters} // ✅ MANTIENE: Usa lo stato dinamico per navigazione
        defaultValuesForCreate={defaultValuesForCreate} // ✅ AGGIUNTO: Passa i valori di default
        onFieldChange={handleFieldChange} // NUOVO: Side-effects per ricalcolo ATT_ValidazioneZod
      />
      <DragDropSequenceManager
        isOpen={isSequenceModalOpen}
        onClose={() => setIsSequenceModalOpen(false)}
        items={sequenceData}
        onSave={handleSaveSequence}
        title="Ordinamento Attributi"
        sequenceField={sequenceField}
        idField={primaryKey}
        displayField="ATT_Nome"
        descriptionField="ATT_Descrizione"
        themeColor="indigo"
      />
    </div>
  );
};

export default AttributiPage;